import React, { useState } from 'react'
// @ts-ignore
import { Card, CardBody, Badge, Avatar } from 'dsm-react-lib'
import { Dropdown, DropdownToggle, DropdownMenu, DropdownItem } from 'reactstrap'
import { PendingIcon } from '../Chatbot/icons/Icons'
import {
  IconDatabase,
  IconFiles,
  IconDownload,
  IconAlertTriangle,
  IconInfoCircle,
  IconTrash,
  IconDotsVertical
} from '@tabler/icons-react'
//@ts-ignore
import imagePng from '../../../assets/images/pages/image.png'
import { getInitials } from '../../../utility/Utils'
import './styles.css'
import { getFileIcon } from '../Chatbot/utils/helpers'
import { isDocumentStuck } from './utils'

export type CardMode = 'files' | 'dataProducts' | 'mixed'

export interface BaseCardItem {
  id: string
  name: string
  owner: string
  dateAdded: string
  tags?: string[]
  creators?: Array<{
    name: string
    avatar: string
  }>
  uploadedBy?: string
  modifiedBy?: {
    name: string
    date: string
  }
  lastUpdated?: string
  status?: string
  smartDocsDocumentId?: string
}

export interface FileCardItem extends BaseCardItem {
  type: 'file'
  source: string
  docType?: string
  size?: string
  sizeFormatted?: string
  extension?: string
  fileUrl?: string
  questions?: string[]
  pdfContent?: {
    title: string
    pages: number
    sections: Array<{
      title: string
      content: string
    }>
  }
}

export interface DataProductCardItem extends BaseCardItem {
  type: 'dataproduct'
  source?: string
  collection?: string
  description?: string
  tier?: string
}

export interface MixedCardItem extends BaseCardItem {
  type: 'file' | 'dataproduct' | 'databox'
  smartDocsDataboxId?: string
  source?: string
  collection?: string
  description?: string
  tier?: string
  securityClassification?: string
  docType?: string
  sizeFormatted?: string
  extension?: string
  filesCount?: number
  dataProductsCount?: number
  totalSizeFormatted?: string
  selectedFilesCount?: number
  selectedDataProductsCount?: number
  selectedItems?: any[] // Store selected files/products for databoxes with partial selection
  fromDataboxId?: string // Mark files/data products that came from databox custom selections
  teamIdentifier?: string // Store team ID for databoxes to handle team-based databoxes in Teams view
  scopeId?: string // Store team GUID (scopeId) for databoxes
  scope?: 'Personal' | 'Team' // Distinguishes the user's own Personal Databox from a Team databox
}

export type CardItem = FileCardItem | DataProductCardItem | MixedCardItem

export interface UnifiedCardViewProps {
  items: CardItem[]
  mode: CardMode
  onItemSelect?: (itemId: string) => void
  onDelete?: (itemId: string) => void
  onEdit?: (itemId: string) => void
  onDownload?: (item: CardItem) => void
  role?: 'Owner' | 'Contributor' | 'Member'
}

const DataboxItems: React.FC<UnifiedCardViewProps> = ({
  items,
  mode,
  onItemSelect,
  onDelete,
  onEdit,
  onDownload,
  role
}) => {
  const [hoveredCard, setHoveredCard] = useState<string | null>(null)
  const [iconHoverIdx, setIconHoverIdx] = useState<number | null>(null)
  const [openDropdownId, setOpenDropdownId] = useState<string | null>(null)

  // Prefer API-provided formatted size and keep safe fallbacks for older payloads.
  const getFileSize = (fileItem: FileCardItem) => {
    return fileItem.sizeFormatted || fileItem.size || '-'
  }

  // Helper function to get icon for mixed mode items
  const getItemIcon = (type: string) => {
    return type === 'file' ? (
      <IconFiles size={24} className="text-primary" />
    ) : (
      <IconDatabase size={24} strokeWidth={2} className="text-info" />
    )
  }

  // Render header section based on mode
  const renderHeader = (item: CardItem, idx: number) => {
    if (mode === 'dataProducts') {
      const dataProductItem = item as DataProductCardItem
      return (
        <div className="flex w-full justify-center">
          <div className="relative w-auto h-[140px] overflow-hidden">
            <span className="shine-effect" />
            <div className="clip-polygon-left ">
              <img
                src={imagePng}
                alt={dataProductItem.name}
                className="w-3/4 h-full object-cover rounded-[5px]"
                onError={(e) => {
                  e.currentTarget.onerror = null
                  e.currentTarget.src = imagePng
                }}
              />
            </div>

            <div className="absolute me-1 pointer-events-none theme-surface z-10 clip-polygon-right" />

            {/* Right colored section with tier badge */}
            <div className="absolute inset-0 polygon-bg">
              <div
                className={`w-full h-full rounded-[5px] ${
                  dataProductItem?.tier === 'Business Optimised'
                    ? 'bg-[var(--tier-optimised)]'
                    : dataProductItem?.tier === 'Curated'
                    ? 'bg-[var(--tier-curated)]'
                    : 'bg-[var(--tier-standard)]'
                }`}
              />
              <div className="absolute tier-info ">
                <div className="text-right mt-1">
                  <span className="text-white/90 text-sm flex justify-end">
                    TIER
                    <IconInfoCircle
                      size={14}
                      className="ml-1 cursor-pointer"
                      stroke="currentColor"
                      data-tooltip-id={`tier-${dataProductItem?.tier}`}
                    />
                  </span>
                  <span className="block text-white text-lg font-extrabold break-words whitespace-pre-line">
                    {dataProductItem?.tier === 'Business Optimised'
                      ? 'BUSINESS\nOPTIMISED'
                      : dataProductItem?.tier === 'Curated'
                      ? 'CURATED'
                      : 'SOURCE'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )
    }

    if (mode === 'mixed') {
      const mixedItem = item as MixedCardItem
      return (
        <div
          className={`flex items-center gap-1 font-bold ${
            mixedItem.type === 'file'
              ? 'text-[var(--type-file-color)]'
              : 'text-[var(--type-data-color)]'
          }`}>
          {getItemIcon(mixedItem.type)}
          {mixedItem.type.toLocaleUpperCase()}
        </div>
      )
    }

    return null
  }

  // Render title and dropdown menu
  const renderTitleAndMenu = (item: CardItem, idx: number) => {
    const isDisabled =
      (item.status === 'Uploaded' || item.status === 'Ingested') &&
      !isDocumentStuck(item.status, item.dateAdded)
    return (
      <div className="flex items-center gap-[10px] w-full">
        {mode === 'files' && getFileIcon(item.name)}
        {(item.status === 'Uploaded' || item.status === 'Ingested') && (
          <PendingIcon size={16} color="var(--color-warning)" />
        )}
        {item.status === 'Failed' && <IconAlertTriangle size={20} color="var(--color-danger)" />}
        <span
          className={`font-bold ${
            mode === 'files' ? 'theme-text-heading' : ''
          } text-xl overflow-hidden text-ellipsis whitespace-nowrap flex-1 ${
            mode === 'dataProducts' ? 'cursor-help' : ''
          }`}
          title={item.name}>
          {item.name}
        </span>
        {role !== 'Member' && !isDisabled && (
          <Dropdown
            isOpen={openDropdownId === `title-${item.id}`}
            toggle={() =>
              setOpenDropdownId(openDropdownId === `title-${item.id}` ? null : `title-${item.id}`)
            }
            className={mode === 'files' ? 'ml-auto' : 'me-1 mb-auto ms-auto'}>
            <DropdownToggle
              tag="span"
              className="cursor-pointer"
              onClick={(e) => {
                e.stopPropagation()
                setHoveredCard(item.id)
              }}>
              {mode === 'mixed' ? (
                <div
                  onMouseEnter={() => setIconHoverIdx(idx)}
                  onMouseLeave={() => setIconHoverIdx(null)}
                  className="flex items-center">
                  <IconDotsVertical
                    size={20}
                    color={iconHoverIdx === idx ? 'var(--color-primary)' : '#878295'}
                  />
                </div>
              ) : (
                <IconDotsVertical size={15} />
              )}
            </DropdownToggle>
            <DropdownMenu>
              {(mode === 'files' || (mode === 'mixed' && item.type === 'file')) &&
                item.status !== 'Failed' &&
                !isDocumentStuck(item.status || '', item.dateAdded || '') && (
                  <>
                    {/* <DropdownItem
                    className={mode === 'files' ? 'w-100 text-primary' : 'w-full text-primary'}>
                    <div
                      className={
                        mode === 'files' ? 'd-flex align-items-center' : 'flex items-center'
                      }>
                      <Edit size={14} />
                      <span className="align-middle ms-2">Edit</span>
                    </div>
                  </DropdownItem> */}
                    <DropdownItem
                      className={
                        mode === 'files'
                          ? 'w-100 theme-dropdown-accent-item'
                          : 'w-full theme-dropdown-accent-item'
                      }
                      onClick={(e) => {
                        e.stopPropagation()
                        onDownload?.(item)
                      }}>
                      <div
                        className={
                          mode === 'files' ? 'd-flex align-items-center' : 'flex items-center'
                        }>
                        <IconDownload size={15} />
                        <span className="align-middle ms-2">Download</span>
                      </div>
                    </DropdownItem>
                  </>
                )}
              <DropdownItem
                className={
                  mode === 'files'
                    ? 'w-100 theme-dropdown-danger-item'
                    : mode === 'mixed'
                    ? 'w-full theme-dropdown-danger-item'
                    : 'w-100 theme-dropdown-danger-item'
                }
                onClick={(e) => {
                  e.stopPropagation()
                  onDelete?.(item.id)
                }}>
                <div
                  className={mode === 'files' ? 'd-flex align-items-center' : 'flex items-center'}>
                  <IconTrash size={14} />
                  <span className="align-middle ms-2">Delete</span>
                </div>
              </DropdownItem>
            </DropdownMenu>
          </Dropdown>
        )}
      </div>
    )
  }

  // Render description/subtext (only for data products)
  const renderDescription = (item: CardItem) => {
    if (mode === 'dataProducts') {
      const dataProductItem = item as DataProductCardItem
      return (
        <span className="subtitle-text word break line-clamp-3 ellipsis">
          {dataProductItem.description ? dataProductItem.description : 'No description available'}
        </span>
      )
    }
    return null
  }

  // Render metadata fields based on mode
  const renderMetadata = (item: CardItem) => {
    if (mode === 'files') {
      const fileItem = item as FileCardItem
      return (
        <div className="grid grid-cols-2 gap-x-8 gap-y-5">
          <div className="flex flex-col items-start">
            <div className="block w-100">
              <h6 className="font-bold">UPLOADED BY</h6>
              {renderCreatorsOrOwner(item)}
            </div>
          </div>
          <div className="flex flex-col items-start">
            <h6 className="font-bold">UPLOADED DATE</h6>
            <div>
              <b className="text-base">
                {new Date(fileItem.modifiedBy?.date || fileItem.dateAdded).toLocaleDateString()}
              </b>
            </div>
          </div>
          <div className="flex flex-col items-start">
            <h6 className="font-bold">DOCUMENT TYPE</h6>
            <div className="flex items-start space-x-2">
              <b className="text-base">{fileItem.docType || '-'}</b>
            </div>
          </div>
          <div className="flex flex-col items-start">
            <h6 className="font-bold">FILE SIZE</h6>
            <div className="flex items-start space-x-2">
              <b className="text-base">{getFileSize(fileItem)}</b>
            </div>
          </div>
          <div className="flex flex-col items-start">
            <h6 className="font-bold">TAGS</h6>
            {renderTags(item)}
          </div>
        </div>
      )
    }

    if (mode === 'dataProducts') {
      const dataProductItem = item as DataProductCardItem
      return (
        <div className="grid grid-cols-2 gap-x-8 gap-y-5">
          <div className="flex flex-col items-start">
            <div className="block w-[120%]">
              <h6 className="font-bold">OWNERS</h6>
              {renderCreatorsOrOwner(item)}
            </div>
          </div>
          <div className="flex flex-col items-start">
            <h6 className="font-bold">DATE MODIFIED</h6>
            <div>
              <b className="text-base">
                {dataProductItem.modifiedBy?.date || dataProductItem.dateAdded
                  ? new Date(
                      dataProductItem.modifiedBy?.date || dataProductItem.dateAdded
                    ).toLocaleDateString()
                  : '-'}
              </b>
            </div>
          </div>
          <div className="flex flex-col items-start">
            <h6 className="font-bold">SOURCE</h6>
            <div className="flex items-start space-x-2">
              <b className="text-base">{dataProductItem.source || 'N/A'}</b>
            </div>
          </div>
          <div className="flex flex-col items-start">
            <h6 className="font-bold">COLLECTION</h6>
            <div className="flex items-start space-x-2">
              <b className="text-base">{dataProductItem.collection || 'N/A'}</b>
            </div>
          </div>
          <div className="flex flex-col items-start">
            <h6 className="font-bold">TAGS</h6>
            {renderTags(item)}
          </div>
        </div>
      )
    }

    if (mode === 'mixed') {
      const mixedItem = item as MixedCardItem
      return (
        <div className="grid grid-cols-1 gap-y-5">
          <div className="flex flex-row items-start gap-1">
            <div className="flex flex-col items-start flex-1">
              <h6 className="font-bold">OWNER</h6>
              {renderCreatorsOrOwner(item)}
            </div>
            <div className="flex flex-col items-start flex-1">
              <h6 className="font-bold">SOURCE</h6>
              <div className="flex items-start">
                <b className="text-lg">{mixedItem.source || 'N/A'}</b>
              </div>
            </div>
          </div>

          <div className="flex flex-col items-start">
            <h6 className="font-bold">TAGS</h6>
            {renderTags(item)}
          </div>
        </div>
      )
    }

    return null
  }

  // Render owner/uploaded by avatar section (shared across all modes)
  const renderCreatorsOrOwner = (item: CardItem) => {
    // Extract displayName safely - handle both string and object formats
    let displayName: string
    const rawOwner: string | { name?: string } | undefined =
      item.uploadedBy || item.modifiedBy?.name || item.owner

    if (typeof rawOwner === 'object' && rawOwner !== null) {
      displayName = (rawOwner as { name?: string })?.name || '-'
    } else {
      displayName = rawOwner || '-'
    }

    if (displayName === '-' || !displayName) {
      return (
        <div className="flex items-center space-x-2">
          <div className="ml-2">
            <b>
              <span className="text-base">-</span>
            </b>
          </div>
        </div>
      )
    }

    return (
      <div className="flex items-center">
        <Avatar content={getInitials(displayName)} className="!ml-0" />
        <div className="ml-0">
          <b>
            <span className="text-base max-w-[150px] truncate" title={displayName}>
              {displayName.length > 8 ? `${displayName.slice(0, 8)}…` : displayName}
            </span>
          </b>
        </div>
      </div>
    )
  }

  // Render tags section (shared across all modes)
  const renderTags = (item: CardItem) => {
    return (
      <div
        className={`flex ${
          mode === 'mixed' ? 'flex-wrap' : 'flex-nowrap'
        } gap-1 overflow-x-auto subtitle-text`}>
        {item.tags && item.tags.length > 0 ? (
          <>
            {item.tags.slice(0, 2).map((tag, index) => {
              const tagLabel =
                typeof tag === 'object' ? (tag as any)?.name || String(tag) : String(tag)
              return (
                <Badge key={index} label={tagLabel} color="#6c757d" variant="light" type="pill" />
              )
            })}
            {item.tags.length > 2 && (
              <Badge
                label={`+${item.tags.length - 2}`}
                color="primary"
                variant="light"
                type="pill"
              />
            )}
          </>
        ) : (
          <span className="text-base">-</span>
        )}
      </div>
    )
  }

  // Render empty state based on mode
  const renderEmptyState = () => {
    if (mode === 'files') {
      return (
        <div className="min-w-[400px] min-h-[304px] flex-shrink-0">
          <Card className="h-100 d-flex align-items-center justify-content-center">
            <div className="text-center py-5">
              <IconFiles size={48} className="theme-text-subheading mb-3" />
              <h5 className="theme-text-subheading">No files found</h5>
              <p className="theme-text-subheading">Upload files to see them here</p>
            </div>
          </Card>
        </div>
      )
    }

    if (mode === 'dataProducts') {
      return (
        <div className="w-100 d-flex justify-content-center align-items-center min-h-[304px]">
          <Card className="d-flex align-items-center justify-content-center border-0 shadow-none">
            <div className="text-center py-5">
              <IconDatabase size={48} className="theme-text-subheading mb-3" />
              <h5 className="theme-text-subheading">No data products found</h5>
              <p className="theme-text-subheading">Create data products to see them here</p>
            </div>
          </Card>
        </div>
      )
    }

    if (mode === 'mixed') {
      return (
        <div className="col-12">
          <Card className="h-100 d-flex align-items-center justify-content-center">
            <div className="text-center py-5">
              <IconDatabase size={48} strokeWidth={2} className="theme-text-subheading mb-3" />
              <h5 className="theme-text-subheading">No items found</h5>
              <p className="theme-text-subheading">Add files or data products to see them here</p>
            </div>
          </Card>
        </div>
      )
    }

    return null
  }

  // Get container classes based on mode
  const getContainerClasses = () => {
    if (mode === 'files') {
      return 'd-flex overflow-auto flex-wrap gap-2'
    }
    if (mode === 'dataProducts') {
      return 'd-flex flex-wrap pt-0 mt-0 overflow-auto'
    }
    if (mode === 'mixed') {
      return 'flex flex-wrap gap-2'
    }
    return ''
  }

  // Get card classes based on mode
  const getCardClasses = () => {
    if (mode === 'files') {
      return '!min-w-[380px] !min-h-[304px] !mb-0 flex-shrink-0 cursor-pointer hover:shadow-lg transition-all duration-300 hover:scale-105 position-relative overflow-visible'
    }
    if (mode === 'dataProducts') {
      return '!w-[400px] min-h-[350px] flex-shrink-0 cursor-pointer hover:shadow-lg transition-all duration-300 hover:scale-105 relative overflow-visible mb-2 me-2'
    }
    if (mode === 'mixed') {
      return 'min-h-[220px] !min-w-[400px] !max-w-[400px] cursor-pointer hover:shadow-lg transition-all duration-300 hover:scale-105 !m-0 pt-0 position-relative overflow-visible'
    }
    return ''
  }

  // Get card body classes based on mode
  const getCardBodyClasses = () => {
    if (mode === 'files' || mode === 'dataProducts') {
      return 'flex p-0 items-start overflow-visible h-100'
    }
    if (mode === 'mixed') {
      return 'flex !p-0 items-start overflow-visible'
    }
    return ''
  }

  // Get inner container classes based on mode
  const getInnerContainerClasses = () => {
    if (mode === 'files') {
      return 'flex flex-col items-center w-1/4 theme-card-border w-[400px] min-h-[100%] overflow-visible'
    }
    if (mode === 'dataProducts') {
      return 'flex flex-col items-center w-1/4 theme-card-border min-w-[400px] min-h-[100%] overflow-visible'
    }
    if (mode === 'mixed') {
      return 'flex flex-col items-center w-full theme-card-border min-h-[100%]'
    }
    return ''
  }

  // Get metadata container classes based on mode
  const getMetadataContainerClasses = () => {
    if (mode === 'files' || mode === 'dataProducts') {
      return 'flex flex-col p-7 w-full h-full rounded-b-lg min-h-[calc(100%-120px)]'
    }
    if (mode === 'mixed') {
      return 'flex flex-col w-full h-full'
    }
    return ''
  }

  return (
    <div className={getContainerClasses()}>
      {items.length === 0
        ? renderEmptyState()
        : items?.map((item, idx) => {
            const isDisabled =
              (item.status === 'Uploaded' ||
                item.status === 'Ingested' ||
                item.status === 'Uploading') &&
              !isDocumentStuck(item.status, item.dateAdded)
            return (
              <Card
                key={item.id || idx}
                className={getCardClasses()}
                style={
                  isDisabled ? { opacity: 0.5, pointerEvents: 'none', cursor: 'not-allowed' } : {}
                }
                onClick={() => !isDisabled && onItemSelect?.(item.id)}>
                <CardBody className={getCardBodyClasses()}>
                  <div className={getInnerContainerClasses()}>
                    {mode !== 'mixed' && (
                      <div className="flex flex-col items-center gap-1 w-full p-2">
                        {renderHeader(item, idx)}
                        {renderTitleAndMenu(item, idx)}
                        {renderDescription(item)}
                      </div>
                    )}
                    {mode === 'mixed' && (
                      <div className="flex-col items-center w-full p-7">
                        <div className="flex items-center justify-between w-full mb-1">
                          {renderHeader(item, idx)}
                          {role !== 'Member' && !isDisabled && (
                            <Dropdown
                              isOpen={openDropdownId === `mixed-${item.id}`}
                              toggle={() =>
                                setOpenDropdownId(
                                  openDropdownId === `mixed-${item.id}` ? null : `mixed-${item.id}`
                                )
                              }>
                              <DropdownToggle
                                tag="span"
                                className="cursor-pointer"
                                onClick={(e) => {
                                  e.stopPropagation()
                                  setHoveredCard(item.id)
                                }}>
                                <div
                                  onMouseEnter={() => setIconHoverIdx(idx)}
                                  onMouseLeave={() => setIconHoverIdx(null)}
                                  className="flex items-center">
                                  <IconDotsVertical
                                    size={20}
                                    color={
                                      iconHoverIdx === idx ? 'var(--color-primary)' : '#878295'
                                    }
                                  />
                                </div>
                              </DropdownToggle>
                              <DropdownMenu>
                                {item.type === 'file' &&
                                  item.status !== 'Failed' &&
                                  !isDocumentStuck(item.status || '', item.dateAdded || '') && (
                                    <>
                                      {/* <DropdownItem className="w-full text-primary">
                                    <div className="flex items-center">
                                      <Edit size={14} />
                                      <span className="align-middle ms-2">Edit</span>
                                    </div>
                                  </DropdownItem> */}
                                      <DropdownItem
                                        className="w-full theme-dropdown-accent-item"
                                        onClick={(e) => {
                                          e.stopPropagation()
                                          onDownload?.(item)
                                        }}>
                                        <div className="flex items-center">
                                          <IconDownload size={15} />
                                          <span className="align-middle ms-2">Download</span>
                                        </div>
                                      </DropdownItem>
                                    </>
                                  )}
                                <DropdownItem
                                  className="w-full theme-dropdown-danger-item"
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    onDelete?.(item.id)
                                  }}>
                                  <div className="flex items-center">
                                    <IconTrash size={14} />
                                    <span className="align-middle ms-2">Delete</span>
                                  </div>
                                </DropdownItem>
                              </DropdownMenu>
                            </Dropdown>
                          )}
                        </div>
                        <div className="flex items-center gap-[10px] w-full py-2">
                          {(item.status === 'Uploaded' ||
                            item.status === 'Ingested' ||
                            item.status === 'Uploading') && (
                            <PendingIcon size={16} color="var(--color-warning)" />
                          )}
                          {item.status === 'Failed' && (
                            <IconAlertTriangle size={20} color="var(--color-danger)" />
                          )}
                          <span
                            className="font-bold text-xl overflow-hidden text-ellipsis whitespace-nowrap flex-1"
                            title={item.name}>
                            {item.name}
                          </span>
                        </div>
                        {renderMetadata(item)}
                      </div>
                    )}
                    {mode !== 'mixed' && (
                      <div className={getMetadataContainerClasses()}>{renderMetadata(item)}</div>
                    )}
                  </div>
                </CardBody>
              </Card>
            )
          })}
    </div>
  )
}

export default DataboxItems
